import { useState, useMemo } from 'react'
import {
  Box, Typography, Chip, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper,
} from '@mui/material'
import { CalendarMonth, Search } from '@mui/icons-material'
import ExportMenu from '../../components/common/ExportMenu'
import { formatNumber } from '../../utils/format'

const DEMO_DATA = [
  { month: 'April', year: 2026, workingDays: 22, holidays: 0, totalHoursAvailable: 352 },
  { month: 'May', year: 2026, workingDays: 21, holidays: 1, totalHoursAvailable: 336 },
  { month: 'June', year: 2026, workingDays: 22, holidays: 0, totalHoursAvailable: 352 },
  { month: 'July', year: 2026, workingDays: 22, holidays: 1, totalHoursAvailable: 336 },
  { month: 'August', year: 2026, workingDays: 21, holidays: 0, totalHoursAvailable: 336 },
  { month: 'September', year: 2026, workingDays: 22, holidays: 1, totalHoursAvailable: 336 },
  { month: 'October', year: 2026, workingDays: 22, holidays: 0, totalHoursAvailable: 352 },
  { month: 'November', year: 2026, workingDays: 20, holidays: 2, totalHoursAvailable: 288 },
  { month: 'December', year: 2026, workingDays: 21, holidays: 1, totalHoursAvailable: 320 },
  { month: 'January', year: 2027, workingDays: 22, holidays: 1, totalHoursAvailable: 336 },
  { month: 'February', year: 2027, workingDays: 20, holidays: 0, totalHoursAvailable: 320 },
  { month: 'March', year: 2027, workingDays: 22, holidays: 0, totalHoursAvailable: 352 },
]

export default function BusinessCalendar() {
  const [search, setSearch] = useState('')
  const [orderBy, setOrderBy] = useState('month')
  const [order, setOrder] = useState('asc')

  const monthOrder = ['January','February','March','April','May','June','July','August','September','October','November','December']

  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc')
    setOrderBy(col)
  }

  const rows = useMemo(() => {
    let filtered = DEMO_DATA.filter((r) =>
      `${r.month} ${r.year}`.toLowerCase().includes(search.toLowerCase()),
    )
    filtered.sort((a, b) => {
      if (orderBy === 'month') {
        const ai = a.year * 100 + monthOrder.indexOf(a.month)
        const bi = b.year * 100 + monthOrder.indexOf(b.month)
        return order === 'asc' ? ai - bi : bi - ai
      }
      const av = a[orderBy], bv = b[orderBy]
      const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv
      return order === 'asc' ? cmp : -cmp
    })
    return filtered
  }, [search, orderBy, order])

  const totalHours = DEMO_DATA.reduce((s, r) => s + r.totalHoursAvailable, 0)

  const columns = [
    { id: 'month', label: 'Month' },
    { id: 'year', label: 'Year', align: 'right' },
    { id: 'workingDays', label: 'Working Days', align: 'right' },
    { id: 'holidays', label: 'Holidays', align: 'right' },
    { id: 'totalHoursAvailable', label: 'Total Hours Available', align: 'right' },
  ]

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CalendarMonth color="primary" />
        <Typography variant="h3" sx={{ flex: 1 }}>Business Calendar</Typography>
        <ExportMenu />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Chip label="FBO Plant Calendar" size="small" variant="outlined" />
        <Typography variant="overline" color="text.secondary">
          As of Mar 10, 2026 · v1 · {DEMO_DATA.length} months
        </Typography>
      </Box>

      <TextField
        size="small"
        placeholder="Search months…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, width: 280 }}
        slotProps={{ input: { startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> } }}
      />

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col.id} align={col.align || 'left'} sx={{ fontWeight: 700 }}>
                  <TableSortLabel
                    active={orderBy === col.id}
                    direction={orderBy === col.id ? order : 'asc'}
                    onClick={() => handleSort(col.id)}
                  >
                    {col.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={`${row.month}-${row.year}`} hover>
                <TableCell sx={{ fontWeight: 600 }}>{row.month}</TableCell>
                <TableCell align="right">{row.year}</TableCell>
                <TableCell align="right">{row.workingDays}</TableCell>
                <TableCell align="right">{row.holidays}</TableCell>
                <TableCell align="right">{formatNumber(row.totalHoursAvailable)}</TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 700 }} colSpan={4}>Total (12 months)</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700 }}>{formatNumber(totalHours)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
